import {
    FlowEventoNotaEmitidaTypeDTO,
    isQualquerTipoSaidaAutorizada,
    shouldShowDialogEventoNota,
} from 'pages/evento-nota-fiscal/evento-nota-fiscal.model';

export enum DocumentoPosVenda {
    NOTA_FISCAL = 'NOTA_FISCAL',
    COMPROVANTE = 'COMPROVANTE',
    EVENTO_NOTA = 'EVENTO_NOTA',
    BOLETO = 'BOLETO',
    CREDIARIO = 'CREDIARIO',
    ROMANEIO = 'ROMANEIO',
    CUPOM_TROCA = 'CUPOM_TROCA',
}

export enum FluxoFinalizacao {
    FISCAL = 'FISCAL',
    OUTROS = 'OUTROS',
}

export enum EncerramentoPosVenda {
    NAVEGA_REGISTRA_VENDA = 'NAVEGA_REGISTRA_VENDA',
    NAVEGA_POR_EVENTO_NOTA = 'NAVEGA_POR_EVENTO_NOTA',
    PERMANECE_NO_PAGAMENTO = 'PERMANECE_NO_PAGAMENTO',
}

export type ParamsFilaDocumentosPosVenda = {
    fluxo: FluxoFinalizacao;
    eventoNotaType: FlowEventoNotaEmitidaTypeDTO | null;
    urlDanfe: string;
    possuiEventoNotaFiscal: boolean;
    boletoURL: string;
    crediario: boolean;
    canImprimirPedido: boolean;
    canImprimirCrediario: boolean;
    canImprimirRomaneio: boolean;
    imprimirRomaneioConfigurado: boolean;
    imprimirCupomTrocaConfigurado: boolean;
    possuiItensParaCupomTroca: boolean;
};

export type FilaDocumentosPosVenda = {
    documentos: Array<DocumentoPosVenda>;
    encerramento: EncerramentoPosVenda;
};

export const DOCUMENTOS_QUE_EXIGEM_PEDIDO_CARREGADO: Array<DocumentoPosVenda> = [
    DocumentoPosVenda.COMPROVANTE,
    DocumentoPosVenda.CREDIARIO,
    DocumentoPosVenda.ROMANEIO,
    DocumentoPosVenda.CUPOM_TROCA,
];

const resolverDocumentoInicial = (
    params: ParamsFilaDocumentosPosVenda,
): DocumentoPosVenda | null => {
    if (params.fluxo === FluxoFinalizacao.OUTROS) {
        return params.canImprimirPedido ? DocumentoPosVenda.COMPROVANTE : null;
    }
    if (!params.eventoNotaType) return null;
    if (
        isQualquerTipoSaidaAutorizada(params.eventoNotaType) &&
        params.urlDanfe.startsWith('http')
    ) {
        return DocumentoPosVenda.NOTA_FISCAL;
    }
    if (shouldShowDialogEventoNota(params.eventoNotaType) && params.possuiEventoNotaFiscal) {
        return DocumentoPosVenda.EVENTO_NOTA;
    }
    return null;
};

const resolverEncerramento = (
    fluxo: FluxoFinalizacao,
    documentoInicial: DocumentoPosVenda | null,
): EncerramentoPosVenda => {
    if (documentoInicial === DocumentoPosVenda.EVENTO_NOTA) {
        return EncerramentoPosVenda.NAVEGA_POR_EVENTO_NOTA;
    }
    if (fluxo === FluxoFinalizacao.FISCAL && documentoInicial === null) {
        return EncerramentoPosVenda.PERMANECE_NO_PAGAMENTO;
    }
    return EncerramentoPosVenda.NAVEGA_REGISTRA_VENDA;
};

// Boleto e crediário só existem nos ramos que hoje passam pela danfe ou pelo comprovante:
// no ramo do dialog de evento e no de erro da Sefaz o fluxo antigo encerrava antes deles.
const emiteBoleto = (
    params: ParamsFilaDocumentosPosVenda,
    documentoInicial: DocumentoPosVenda | null,
): boolean => {
    if (!params.boletoURL) return false;
    return (
        params.fluxo === FluxoFinalizacao.OUTROS ||
        documentoInicial === DocumentoPosVenda.NOTA_FISCAL
    );
};

const emiteCrediario = (
    params: ParamsFilaDocumentosPosVenda,
    documentoInicial: DocumentoPosVenda | null,
): boolean => {
    if (!params.crediario) return false;
    if (documentoInicial === DocumentoPosVenda.NOTA_FISCAL) return true;
    if (documentoInicial === DocumentoPosVenda.COMPROVANTE) return params.canImprimirCrediario;
    return false;
};

const emiteRomaneio = (params: ParamsFilaDocumentosPosVenda): boolean =>
    params.canImprimirRomaneio && params.imprimirRomaneioConfigurado;

const emiteCupomTroca = (
    params: ParamsFilaDocumentosPosVenda,
    documentoInicial: DocumentoPosVenda | null,
): boolean =>
    documentoInicial === DocumentoPosVenda.NOTA_FISCAL &&
    params.imprimirCupomTrocaConfigurado &&
    !!params.eventoNotaType &&
    isQualquerTipoSaidaAutorizada(params.eventoNotaType) &&
    params.possuiItensParaCupomTroca;

export const montarFilaDocumentosPosVenda = (
    params: ParamsFilaDocumentosPosVenda,
): FilaDocumentosPosVenda => {
    const documentoInicial = resolverDocumentoInicial(params);
    const documentos: Array<DocumentoPosVenda> = [];

    if (documentoInicial) documentos.push(documentoInicial);
    if (emiteBoleto(params, documentoInicial)) documentos.push(DocumentoPosVenda.BOLETO);
    if (emiteCrediario(params, documentoInicial)) documentos.push(DocumentoPosVenda.CREDIARIO);
    if (emiteRomaneio(params)) documentos.push(DocumentoPosVenda.ROMANEIO);
    if (emiteCupomTroca(params, documentoInicial)) documentos.push(DocumentoPosVenda.CUPOM_TROCA);

    return {
        documentos,
        encerramento: resolverEncerramento(params.fluxo, documentoInicial),
    };
};

export const exigePedidoCarregado = (documentos: Array<DocumentoPosVenda>): boolean =>
    documentos.some(documento => DOCUMENTOS_QUE_EXIGEM_PEDIDO_CARREGADO.includes(documento));
